import type { UserGroup } from "admin/types/interfaces/user";
import { Users } from "lucide-react";
import useUser from "~/contexts/user";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { groupLabel, useUserGroups } from "./useUserGroups";

const GroupRow = ({ group }: { group: UserGroup }) => (
  <li className="flex items-center gap-3 px-4 py-3">
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
      <Users className="size-4" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium text-foreground">{groupLabel(group)}</p>
      {group.description && (
        <p className="truncate text-xs text-muted-foreground">{group.description}</p>
      )}
    </div>
  </li>
);

/**
 * The profile's Groups section: which groups the user belongs to. Read-only —
 * membership is granted by an administrator, and it is what most of the user's
 * data and tool access is attached to.
 */
export const ProfileGroupsSection = () => {
  const user = useUser();
  const { groups, isLoading, error } = useUserGroups(user?.email);

  return (
    <SettingsSection title="Groups" description="Your group memberships">
      {isLoading ? (
        <div className="flex flex-col gap-2 p-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-11 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : error ? (
        <div className="px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">Couldn&rsquo;t load your groups.</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="px-4 py-10 text-center">
          <p className="text-sm font-medium text-foreground">You&rsquo;re not in any groups.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Ask an administrator to add you to one if you need access to shared data.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {groups.map((group) => (
            <GroupRow key={group.name} group={group} />
          ))}
        </ul>
      )}
    </SettingsSection>
  );
};
